import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useMediaQuery } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AddIcon from "@mui/icons-material/Add";
import { Button, Grid, TextField, CircularProgress } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { CODES } from "../../../consts/codes";
import { useSelector } from "react-redux";
import { getUser, getUserStatus } from "../../../pages/userSlice";
import { REGEXP } from "../../../consts/regexp";
import {
  GetDynamicReportConf,
  CreateDynamicReportConf,
  UpdateDynamicReportConf,
} from "../../../services/user/infonavit/adminDynamicReport";
import ModalInfo from "../../common/Modals/modalInfo";
import {
  stringToDateTimeString,
  stringToDateString,
} from "../../../utils/functions";

/**
 * Const
 */
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
export const ConfInfonavitRequestsForm = ({
  nrpsData,
  isLoadingData,
  setIsLoadingData,
}) => {
  const [confData, setConfData] = useState(null);

  const [openModalInfo, setOpenModalInfo] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [saveData, setSaveData] = useState({});

  const [loadingSave, setLoadingSave] = useState(false);

  const matches = useMediaQuery("(min-width:1670px)");

  /**
   * t of useTranslation to translate
   */
  const { t } = useTranslation();

  const selectAllItem = {
    label: t("App.infonavitRequests.filter.selectAll"),
    value: t("App.infonavitRequests.filter.selectAll"),
  };

  /**
   * Info of the user who logs into the website
   */
  const userInfo = useSelector(getUser);
  const userStatus = useSelector(getUserStatus);

  /**
   * Schema
   */
  const schema = yup.object().shape({
    daysFrequency: yup
      .string()
      .max(3, t("App.validationMessages.maxCharacters", { number: 3 }))
      .required(t("App.validationMessages.required"))
      .matches(REGEXP.ONLY_NUMBERS, t("App.validationMessages.onlyNumber")),
    dateIni: yup
      .string()
      .nullable()
      .required(t("App.validationMessages.required")),
    nrps: yup
      .array()
      .test(
        "at-least-one",
        t("App.validationMessages.atLeastOne"),
        (value, context) => {
          const isNrpsCheck = getValues("nrpsCheck");

          return isNrpsCheck || value.length > 0;
        }
      ),
  });

  /**
   * useForm to resolver the schema validation
   */
  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
    formState,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      daysFrequency: "0",
      dateIni: `${new Date().getFullYear()}-01-01`,
      lastDateIni: "",
      nrps: [],
      nrpsCheck: false,
      downloadAllHistory: true,
      downloadSpecifiedEmployees: false,
    },
  });

  /**
   * Use Effect
   */
  /**
   * Carga los usuarios
   */
  useEffect(() => {
    if (userStatus === "succeeded") fetchConfiguration();
  }, [userStatus]);

  /**
   * Función que consulta y settea la información de los usuarios
   */
  const fetchConfiguration = async () => {
    try {
      const mainUser = userInfo.mainUser;

      setIsLoadingData(true);

      const {
        status,
        data: { responseStatus, response },
      } = await GetDynamicReportConf(mainUser);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        if (response !== null) {
          setValue(
            "dateIni",
            response.initialConsultingDate ??
              `${new Date().getFullYear()}-01-01`
          );
          setValue("lastDateIni", response.lastConsultDate ?? "");
          setValue("daysFrequency", response.consultFrequency ?? "0");
          setValue("nrpsCheck", response.allNrps ?? false);
          setValue("downloadAllHistory", response.allHystory ?? false);
          setValue(
            "downloadSpecifiedEmployees",
            response.allEmployees ?? false
          );

          const selectedNrps = (response.nrps ?? []).map((v) => {
            return { value: v, label: v };
          });
          setValue("nrps", selectedNrps);
        }

        setConfData(response);
      } else {
        setConfData(null);
      }

      setIsLoadingData(false);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      setConfData(null);
      console.log("====================================");
    }
  };

  /**
   * Función que retorna el título del modal según código de respuesta
   * @param {Number} responseCode código de respuesta
   * @returns título según código
   */
  const handleModalTitle = (responseCode) => {
    if (responseCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST) {
      return "";
    } else if (responseCode === 1001) {
      return t("App.userDetail.modalTitle.title2");
    } else if (responseCode === 1002) {
      return t("App.userDetail.modalTitle.title1");
    } else {
      return "";
    }
  };

  /**
   *
   * Function to edit dynamic report configuration
   *
   * @param {Object} payload
   */
  const saveChanges = async (payload) => {
    try {
      setLoadingSave(true);

      const saveFunction =
        confData === null ? CreateDynamicReportConf : UpdateDynamicReportConf;

      const {
        status,
        data: { responseStatus, response },
      } = await saveFunction(payload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        fetchConfiguration();
      }

      setModalTitle(handleModalTitle(responseStatus.statusCode));
      setOpenModalInfo(true);
      setSaveData({ data: { responseStatus, response }, status });

      setLoadingSave(false);
    } catch (error) {
      setLoadingSave(false);
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  /**
   * filterOptions para el autocomplete de compañías
   */
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 200,
  });

  /**
   *
   * Función que se acciona cuando se cambia el valor del autocomplete de NRPs para cambiar el valor del state de nrpsSelected que es lo que se muestra seleccionado en el autcomplete
   * También funciona para seleccionar todos los valores cuando se selecciona la opción de 'Seleccionar todos'
   *
   * @param {Event} event
   * @param {Values} values nrps
   * @param {String} reason tipo de selección; selectOption, removeOption
   * @param {Object} detail opción elegida
   */
  const onFileChange = (event, values, reason, detail) => {
    let includeSelectAll = false;
    let selectedRows = [];

    for (const row of values) {
      if (row.value === t("App.infonavitRequestsConf.form.selectAll")) {
        includeSelectAll = true;
      }
    }

    if (includeSelectAll && reason === "selectOption") {
      selectedRows = nrpsData;
    } else {
      selectedRows = values;
    }

    if (detail) {
      const option = detail.option;
      if (
        reason === "removeOption" &&
        option.value === t("App.infonavitRequestsConf.form.selectAll")
      ) {
        selectedRows = [];
      }
    }

    return selectedRows;
  };

  /**
   *
   * Function to save changes
   *
   * @param {Object} data
   */
  const handleSaveChanges = async (data) => {
    const tempNrpsList = data.nrps.filter(
      (item) =>
        item.label !== t("App.infonavitDynamicReport.filterFields.selectAll")
    );

    let payload = {
      mainUser: userInfo.mainUser,
      consultFrequency: parseInt(data.daysFrequency),
      nrps: tempNrpsList.map((item) => item.value),
      allNrps: data.nrpsCheck,
      allHystory: data.downloadAllHistory,
      allEmployees: data.downloadSpecifiedEmployees,
      initialConsultingDate: stringToDateTimeString(data.dateIni, true),
    };
    if (confData !== null) payload = { ...confData, ...payload };

    return saveChanges(payload);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <form
          id="infonavit-request-conf-form"
          onSubmit={handleSubmit(handleSaveChanges)}
        >
          <Grid
            container
            justifyContent="flex-start"
            alignItems="flex-start"
            direction="column"
            gap="1rem"
          >
            <Grid item style={{ marginBottom: "1rem" }}>
              <Grid
                container
                gap={{ lg: "4rem", xs: "2rem" }}
                alignItems={{ lg: "center", xs: "flex-start" }}
              >
                {/* -------- DaysFrequency -------  */}
                <Grid item xs={12} xl="auto">
                  <Grid
                    container
                    gap={{ md: "2rem", xs: "1rem" }}
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    direction={{ md: "row", xs: "column" }}
                  >
                    <Grid item>
                      <label className="default-text_black__bold__sm default-text_black__bold">
                        {t("App.infonavitRequestsConf.form.daysFrequency")}
                      </label>
                    </Grid>

                    <Grid item>
                      <Controller
                        name="daysFrequency"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField
                            onChange={onChange}
                            value={value}
                            type="number"
                            className={
                              errors.daysFrequency
                                ? "select__filters__sm select__filters__errors"
                                : "select__filters__sm"
                            }
                            label={t(
                              "App.infonavitRequestsConf.form.daysFrequencyPlaceholder"
                            )}
                            FormHelperTextProps={{
                              style: {
                                color: "#e52900",
                              },
                            }}
                            helperText={
                              errors.daysFrequency
                                ? errors.daysFrequency.message
                                : ""
                            }
                            InputLabelProps={{
                              style: {
                                margin: "-5px 0 0 -10px",
                                padding: "0 0 0 5px",
                                color: "#8c8d9d",
                                fontSize: 15,
                              },
                            }}
                            inputProps={{ min: 1, max: 30 }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* -------- FechaIni -------  */}
                <Grid item xs={12} sm lg="auto">
                  <Grid
                    container
                    gap={{ md: "2rem", xs: "1rem" }}
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    direction={{ md: "row", xs: "column" }}
                  >
                    <Grid item>
                      <label className="default-text_black__bold__sm default-text_black__bold">
                        {t("App.infonavitRequestsConf.form.dateIni")}
                      </label>
                    </Grid>

                    <Grid item>
                      <Controller
                        defaultValue=""
                        control={control}
                        name="dateIni"
                        render={({ field: { onChange, value } }) => (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              className={
                                errors.daysFrequency
                                  ? "select__filters__sm select--fixDate select__filters__errors"
                                  : "select__filters__sm select--fixDate"
                              }
                              minDate="2005-01-07"
                              maxDate={new Date()}
                              inputFormat="YYYY-MM-DD"
                              sx={{
                                translate: "0px -5px",
                              }}
                              value={watch("dateIni") ? watch("dateIni") : null}
                              onChange={onChange}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  onKeyDown={(event) => event.preventDefault()}
                                  onKeyUp={(event) => event.preventDefault()}
                                  label={t(
                                    "App.infonavitRequestsConf.form.dateIniPlaceholder"
                                  )}
                                  InputLabelProps={{
                                    style: {
                                      margin: "-5px 0 0 -10px",
                                      padding: "0 0 0 5px",
                                      color: "#8c8d9d",
                                      fontSize: 15,
                                    },
                                  }}
                                  FormHelperTextProps={{
                                    style: {
                                      color: "#e52900",
                                    },
                                  }}
                                  helperText={
                                    errors.dateIni ? errors.dateIni.message : ""
                                  }
                                />
                              )}
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </Grid>

                    {/* <Grid item>
                      <label className="form__label label__error-color">
                        {errors.dateIni ? errors.dateIni.message : ""}
                      </label>
                    </Grid> */}
                  </Grid>
                </Grid>

                {/* -------- LastFechaIni -------  */}
                <Grid item xs={12} sm lg="auto">
                  <label className="default-text_black__bold__sm default-text_black__bold">
                    {t("App.infonavitRequestsConf.form.lastDateIni")}

                    <br />

                    {getValues("lastDateIni")
                      ? stringToDateString(getValues("lastDateIni"))
                      : ""}
                  </label>
                </Grid>
              </Grid>
            </Grid>

            <Grid item style={{ marginBottom: "1rem" }}>
              <Grid
                container
                gap="2rem"
                alignItems={{ lg: "center", xs: "flex-end" }}
              >
                {/* -------- NRPs -------- */}
                <Grid item>
                  <Grid
                    container
                    gap={{ md: "2rem", xs: "1rem" }}
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    direction={{ md: "row", xs: "column" }}
                  >
                    <Grid item>
                      <label className="default-text_black__bold__sm default-text_black__bold">
                        {t("App.infonavitRequestsConf.form.nrps")}
                      </label>
                    </Grid>

                    <Grid item>
                      <Controller
                        name="nrps"
                        render={({ field: { onChange, value } }) => (
                          <Autocomplete
                            noOptionsText={t(
                              "App.validationMessages.noMatchFilter"
                            )}
                            className={
                              errors.nrps
                                ? "select__filters__sm select--fixAutocomplete select__filters__errors"
                                : "select__filters__sm select--fixAutocomplete"
                            }
                            options={nrpsData}
                            value={value || []}
                            multiple
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            filterOptions={filterOptions}
                            disableCloseOnSelect
                            limitTags={1}
                            onChange={(event, values, reason, detail) => {
                              onChange(
                                onFileChange(event, values, reason, detail)
                              );
                            }}
                            getOptionLabel={(option) => option.label}
                            renderOption={(props, option, { selected }) => (
                              <li key={option.label} {...props}>
                                <Checkbox
                                  icon={icon}
                                  className="autoComplete__checkbox"
                                  checkedIcon={checkedIcon}
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                />
                                {option.label}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t(
                                  "App.infonavitRequestsConf.form.nrpsPlaceholder"
                                )}
                                FormHelperTextProps={{
                                  style: {
                                    color: "#e52900",
                                  },
                                }}
                                helperText={
                                  errors.nrps ? errors.nrps.message : ""
                                }
                              />
                            )}
                            renderTags={(values) => {
                              const size = values.filter(
                                (item) => item.value !== selectAllItem.value
                              ).length;
                              const label =
                                size > 1
                                  ? t("App.autocomplete.selected")
                                  : t("App.autocomplete.oneSelected");

                              return (
                                <Chip
                                  label={`${size} ${label}`}
                                  className="autocomplete-chip"
                                  variant="filled"
                                />
                              );
                            }}
                          />
                        )}
                        control={control}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* -------- SelectAllNRPs -------- */}
                <Grid item>
                  <Grid
                    container
                    direction="row-reverse"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <Grid item>
                      <label className="default-text_black__bold__sm default-text_black__bold">
                        {t("App.infonavitRequestsConf.form.nrpsCheck")}
                      </label>
                    </Grid>

                    <Grid item>
                      <Controller
                        name="nrpsCheck"
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            icon={icon}
                            className="autoComplete__checkbox"
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={value}
                            // value={
                            //   watch("nrpsCheck") ? watch("nrpsCheck") : null
                            // }
                            onChange={onChange}
                          />
                        )}
                        control={control}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item>
              <Grid container alignItems="center">
                {/* -------- DownloadAllHistory -------- */}
                <Grid item>
                  <Grid
                    container
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <Grid item xs="auto">
                      <Controller
                        name="downloadAllHistory"
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            icon={icon}
                            className="autoComplete__checkbox"
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={value}
                            onChange={(params) => {
                              onChange(params);
                              setValue("downloadSpecifiedEmployees", false);
                            }}
                          />
                        )}
                        control={control}
                      />
                    </Grid>

                    <Grid item xs>
                      <label className="default-text_black__bold__sm default-text_black__bold">
                        {t("App.infonavitRequestsConf.form.downloadAllHistory")}
                      </label>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item>
              <Grid container alignItems="center">
                {/* -------- DownloadSpecifiedEmployees -------- */}
                <Grid item>
                  <Grid
                    container
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <Grid item xs="auto">
                      <Controller
                        name="downloadSpecifiedEmployees"
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            icon={icon}
                            className="autoComplete__checkbox"
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={value}
                            onChange={(params) => {
                              onChange(params);
                              setValue("downloadAllHistory", false);
                            }}
                          />
                        )}
                        control={control}
                      />
                    </Grid>

                    <Grid item xs>
                      <label className="default-text_black__bold__sm default-text_black__bold">
                        {t(
                          "App.infonavitRequestsConf.form.downloadSpecifiedEmployees"
                        )}
                      </label>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Grid>

      <Grid item xs={12} justifyContent="center" marginTop="1rem">
        <Grid container justifyContent="center">
          <Button
            className="btn__applyFilter btn__applyFilter--no-margin"
            type="submit"
            form="infonavit-request-conf-form"
            startIcon={loadingSave && isLoadingData ? <AddIcon /> : ""}
          >
            {!loadingSave && !isLoadingData ? (
              t("App.infonavitRequestsConf.saveBtn")
            ) : (
              <CircularProgress size={21} color="inherit" />
            )}
          </Button>
        </Grid>
      </Grid>

      <ModalInfo
        title={modalTitle}
        responseData={saveData}
        onClose={() => {
          setOpenModalInfo(false);
        }}
        open={openModalInfo}
      />
    </Grid>
  );
};
